import { showToast } from "@/lib/utils/toast";
import { dataEntryService } from "../services/data-entry.services";
import { entryUIStore } from "../stores/entry-ui.store";
import { promiseWithMinDelay } from "@/lib/utils/promiseWithMinDelay";
import { loadDataEntries, loadDisclosurePresence } from "./load.ops";

export const bulkCreateDataEntries = async (entries) => {
  const { setCreatingEntries } = entryUIStore;
  setCreatingEntries(true);

  try {
    const createPromise = dataEntryService.bulkCreateDataEntries(entries);

    showToast.promise(createPromise, {
      success: "Successfully created entries",
      loading: "Creating entries",
      error: (err) => err.message || "Failed to create entries",
    });

    const data = await promiseWithMinDelay(createPromise);
    if (data) {
      await loadDataEntries();
      loadDisclosurePresence();
      return true;
    }
  } catch {
    return false;
  } finally {
    setCreatingEntries(false);
  }
};

export const bulkUpdateDataEntries = async (entries) => {
  const { setUpdatingEntry } = entryUIStore;
  setUpdatingEntry(true);

  try {
    const updatePromise = dataEntryService.bulkUpdateDataEntries(entries);

    showToast.promise(updatePromise, {
      success: "Successfully updated entries",
      loading: "Updating entries",
      error: (err) => err.message || "Failed to update entries",
    });

    const data = await promiseWithMinDelay(updatePromise);
    if (data) {
      await loadDataEntries();
      return true;
    }
  } catch {
    return false;
  } finally {
    setUpdatingEntry(false);
  }
};

export const updateDataEntry = async (entryId, payload) => {
  const { setUpdatingEntry } = entryUIStore;
  setUpdatingEntry(true);

  try {
    const updatePromise = dataEntryService.updateDataEntry(entryId, payload);

    showToast.promise(updatePromise, {
      success: "Successfully updated entry",
      loading: "Updating entry",
      error: (err) => err.message || "Failed to update entry",
    });

    await promiseWithMinDelay(updatePromise);
    await loadDataEntries();
    return true;
  } catch {
    return false;
  } finally {
    setUpdatingEntry(false);
  }
};

/**
 * Composite save for EditSubEntriesDialog.
 * Handles bulk update, bulk create, and soft-delete in one go.
 */
export const saveSubEntries = async ({
  toUpdate = [],
  toCreate = [],
  toDelete = [],
}) => {
  const { setUpdatingEntry } = entryUIStore;
  setUpdatingEntry(true);

  try {
    const promises = [];

    if (toUpdate.length) {
      promises.push(dataEntryService.bulkUpdateDataEntries(toUpdate));
    }
    if (toCreate.length) {
      promises.push(dataEntryService.bulkCreateDataEntries(toCreate));
    }
    for (const id of toDelete) {
      promises.push(dataEntryService.softDeleteDataEntry(id));
    }

    const combined = Promise.all(promises);

    showToast.promise(combined, {
      success: "Successfully saved entries",
      loading: "Saving entries",
      error: (err) => err.message || "Failed to save entries",
    });

    await promiseWithMinDelay(combined);
    await loadDataEntries();
    loadDisclosurePresence();
    return true;
  } catch {
    return false;
  } finally {
    setUpdatingEntry(false);
  }
};

/**
 * Sync NXMap emissions with existing data entries (create new + update changed).
 */
export const syncFromNxmap = async ({
  framework,
  year,
  orgId,
  disclosureId,
}) => {
  const { setCreatingEntries } = entryUIStore;
  setCreatingEntries(true);

  try {
    const syncPromise = dataEntryService.syncNxmapEntries({
      framework,
      year,
      orgId,
      disclosureId,
      confirm: true,
    });

    showToast.promise(syncPromise, {
      success: (data) => {
        const parts = [];
        if (data?.created) parts.push(`${data.created} created`);
        if (data?.updated) parts.push(`${data.updated} updated`);
        return parts.length
          ? `NXMap sync: ${parts.join(", ")}`
          : "NXMap sync: no changes";
      },
      loading: "Syncing from NXMap...",
      error: "Failed to sync from NXMap",
    });

    const data = await promiseWithMinDelay(syncPromise);
    if (data) {
      await loadDataEntries();
      loadDisclosurePresence();
      return data;
    }
    return null;
  } catch {
    showToast.error("Failed to sync from NXMap");
    return null;
  } finally {
    setCreatingEntries(false);
  }
};

/**
 * Sync a single NXMap-imported entry with the latest NXMap value.
 */
export const syncNxmapEntry = async (entryId) => {
  const { setUpdatingEntry } = entryUIStore;
  setUpdatingEntry(true);

  try {
    const syncPromise = dataEntryService.syncNxmapEntry(entryId);

    showToast.promise(syncPromise, {
      success: (data) => {
        if (data?.updated) {
          return `Updated: ${data.oldValue} → ${data.newValue} ${data.unit || ""}`;
        }
        if (data?.noMatch) return "No matching NXMap data found";
        return "Already up to date";
      },
      loading: "Syncing from NXMap...",
      error: "Failed to sync from NXMap",
    });

    const data = await promiseWithMinDelay(syncPromise);
    if (data?.updated) {
      await loadDataEntries();
    }
    return true;
  } catch {
    return false;
  } finally {
    setUpdatingEntry(false);
  }
};

export const softDeleteDataEntry = async (entryId) => {
  const { setDeletingEntry } = entryUIStore;
  setDeletingEntry(true);

  try {
    const deletePromise = dataEntryService.softDeleteDataEntry(entryId);

    showToast.promise(deletePromise, {
      success: "Successfully deleted entry",
      loading: "Deleting entry",
      error: "Failed to delete entry",
    });

    await promiseWithMinDelay(deletePromise);
    await loadDataEntries();
    loadDisclosurePresence();
    return true;
  } catch {
    return false;
  } finally {
    setDeletingEntry(false);
  }
};

export const bulkSoftDeleteDataEntries = async (entryIds = []) => {
  const { setDeletingEntry } = entryUIStore;
  if (!entryIds.length) return false;
  setDeletingEntry(true);

  try {
    const deletePromise = dataEntryService.bulkSoftDeleteDataEntries(entryIds);

    showToast.promise(deletePromise, {
      success: `Successfully deleted ${entryIds.length} entries`,
      loading: "Deleting entries",
      error: "Failed to delete entries",
    });

    await promiseWithMinDelay(deletePromise);
    await loadDataEntries();
    loadDisclosurePresence();
    return true;
  } catch {
    return false;
  } finally {
    setDeletingEntry(false);
  }
};
